"use client";
import { ReasoningStyleSelector } from "@/components/chat/ReasoningStyleSelector";

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import ReactMarkdown from "react-markdown";
import {
  AlertTriangle,
  ArrowUp,
  Check,
  ChevronDown,
  ChevronRight,
  CircleStop,
  FileOutput,
  Folder,
  LoaderCircle,
  MessageSquareText,
  Mic,
  Plus,
  RotateCcw,
  Scale,
  Search,
  SearchCheck,
  Settings2,
  ShieldAlert,
  X,
} from "lucide-react";
import { ArtifactCanvas } from "@/components/chat/ArtifactCanvas";
import { getAgentSessionHref } from "@/lib/agentHub";
import { useLanguage } from "@/components/LanguageProvider";
import { SERVICE_PROMPT_PLACEHOLDERS } from "@/lib/promptExamples";
import {
  SEARCH_WORKSPACE_CONFIGS,
  type SearchWorkspaceAgentId,
  type SearchWorkspaceConfig,
} from "./searchWorkspaceContract";
import {
  useSearchWorkspaceRunController,
  type SearchRunAction,
  type SearchRunStage,
} from "./useSearchWorkspaceRunController";
import type { AgentSessionListItem } from "@/lib/agentRunTransport";

type MobileTab = "progress" | "result";

type SpeechRecognitionResultLike = {
  isFinal: boolean;
  0: { transcript: string };
};

type SpeechRecognitionEventLike = {
  results: ArrayLike<SpeechRecognitionResultLike>;
};

type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onstart: (() => void) | null;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
};

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

const durationLabel = (durationMs?: number) => {
  if (!durationMs) return "";
  if (durationMs < 1_000) return `${durationMs}ms`;
  const seconds = Math.round(durationMs / 1_000);
  return seconds < 60
    ? `${seconds}초`
    : `${Math.floor(seconds / 60)}분 ${seconds % 60}초`;
};

const stageTone: Record<SearchRunStage["status"], string> = {
  pending: "border-gray-200 bg-white dark:border-[#2a2a2e] dark:bg-[#18181a]",
  running:
    "border-seexr-200 bg-white dark:border-seexr-900/60 dark:bg-[#18181a]",
  complete:
    "border-gray-200 bg-gray-50/60 dark:border-[#2a2a2e] dark:bg-[#18181a]",
  error:
    "border-gray-300 bg-gray-50/80 dark:border-[#3a3a3f] dark:bg-[#18181a]",
};

function WorkspaceIcon({
  tag,
  size = 18,
}: {
  tag: SearchWorkspaceConfig["artifactTag"];
  size?: number;
}) {
  return tag === "laws" ? <Scale size={size} /> : <ShieldAlert size={size} />;
}

function StageIcon({
  id,
  size = 18,
}: {
  id: SearchRunStage["id"];
  size?: number;
}) {
  if (id === "verify") return null;
  if (id === "search") return <Search size={size} />;
  return <FileOutput size={size} />;
}

function ActionPanel({ action }: { action: SearchRunAction }) {
  const automaticOpen = action.status === "running";
  const [toggle, setToggle] = useState({
    status: action.status,
    open: automaticOpen,
  });
  const open = toggle.status === action.status ? toggle.open : automaticOpen;
  const hasDetails = Boolean(action.inputPreview || action.resultPreview);

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200/80 bg-gray-50/60 dark:border-[#303034] dark:bg-black/10">
      <button
        type="button"
        aria-expanded={open}
        disabled={!hasDetails}
        onClick={() => setToggle({ status: action.status, open: !open })}
        className="flex w-full items-center gap-3 px-3 py-2.5 text-left disabled:cursor-default"
      >
        <span
          className={`inline-flex shrink-0 text-gray-400 dark:text-gray-500 ${action.status === "running" ? "agent-action-loading-icon" : ""}`}
        >
          {action.status === "error" ? (
            <AlertTriangle size={16} strokeWidth={1.8} />
          ) : (
            <SearchCheck size={16} strokeWidth={1.8} />
          )}
        </span>
        <span
          className={`min-w-0 flex-1 truncate text-[12px] font-bold ${action.status === "running" ? "agent-action-loading-text" : "text-gray-600 dark:text-gray-400"}`}
        >
          {action.status === "running"
            ? `${action.title} 중`
            : action.status === "error"
              ? `${action.title} 실패`
              : action.title}
        </span>
        {action.durationMs ? (
          <span className="shrink-0 text-[10px] font-bold text-gray-400">
            {durationLabel(action.durationMs)}
          </span>
        ) : null}
        {hasDetails && (
          <ChevronDown
            size={15}
            className={`shrink-0 text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        )}
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-200 ${open && hasDetails ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden">
          <div className="max-h-72 space-y-2.5 overflow-y-auto border-t border-gray-200/70 px-3 py-3 custom-scrollbar dark:border-[#303034]">
            {action.inputPreview && (
              <div>
                <p className="mb-1 text-[10px] font-black text-gray-400">
                  검색한 조건
                </p>
                <p className="whitespace-pre-wrap text-[12px] leading-5 text-gray-600 dark:text-gray-300">
                  {action.inputPreview}
                </p>
              </div>
            )}
            {action.resultPreview && (
              <div>
                <p className="mb-1 text-[10px] font-black text-gray-400">
                  확인한 결과
                </p>
                <p className="whitespace-pre-wrap text-[12px] leading-5 text-gray-600 dark:text-gray-300">
                  {action.resultPreview}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SelectionBadge({
  value,
  onClick,
  active,
  selected,
  onClear,
}: {
  value: string;
  onClick: () => void;
  active?: boolean;
  selected?: boolean;
  onClear?: () => void;
}) {
  return (
    <div
      className={`group relative inline-flex min-w-0 items-center rounded-full border text-left text-[12px] font-bold transition-colors ${selected ? "border-seexr-primary/35 bg-seexr-solid/10" : active ? "border-gray-400 bg-white dark:border-gray-500 dark:bg-[#1d1d20]" : "border-gray-200 bg-white hover:border-gray-300 dark:border-[#333338] dark:bg-[#1d1d20]"} ${selected ? "text-seexr-primary" : "text-gray-600 dark:text-gray-300"}`}
    >
      <button
        type="button"
        onClick={onClick}
        className="inline-flex min-w-0 items-center gap-1.5 py-2 pl-3 pr-3"
      >
        <span className="max-w-[150px] truncate">{value}</span>
        {!onClear && (
          <ChevronDown size={13} className="shrink-0 text-gray-400" />
        )}
      </button>
      {onClear && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onClear();
          }}
          aria-label={`${value} 선택 해제`}
          className="absolute right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-seexr-solid text-white opacity-0 shadow-[0_0_0_3px_rgba(255,255,255,0.9)] transition-all group-hover:opacity-100 focus-visible:opacity-100 dark:shadow-[0_0_0_3px_rgba(29,29,32,0.95)]"
        >
          <X size={13} strokeWidth={2.8} />
        </button>
      )}
    </div>
  );
}

function SelectionMenu({
  options,
  value,
  onSelect,
}: {
  options: string[];
  value?: string | null;
  onSelect: (value: string) => void;
}) {
  return (
    <div className="absolute left-0 top-[calc(100%+8px)] z-40 max-h-64 min-w-[220px] overflow-y-auto rounded-2xl border border-gray-200 bg-white p-2 shadow-2xl dark:border-[#303034] dark:bg-[#202023]">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onSelect(option)}
          className={`flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-[13px] font-bold transition-colors ${value === option ? "bg-seexr-solid/10 text-seexr-primary" : "text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-[#2a2a2e]"}`}
        >
          <span className="min-w-0 flex-1 truncate">{option}</span>
          {value === option && <Check size={15} className="shrink-0" />}
        </button>
      ))}
    </div>
  );
}

function GeneratingGraphic({
  config,
  stageLabel,
}: {
  config: SearchWorkspaceConfig;
  stageLabel: string;
}) {
  return (
    <div className="relative flex h-full min-h-[520px] items-center justify-center overflow-hidden bg-[#f7f8fa] dark:bg-[#111112]">
      <div className="relative flex h-44 w-44 items-center justify-center">
        <span className="absolute inset-0 animate-ping rounded-full bg-seexr-solid/10 [animation-duration:2.4s]" />
        <span className="absolute inset-6 animate-ping rounded-full bg-seexr-solid/15 [animation-duration:2.4s] [animation-delay:0.5s]" />
        <span className="absolute inset-12 rounded-full bg-seexr-solid/10" />
        <span className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-seexr-primary shadow-lg dark:bg-[#1b1b1e]">
          <WorkspaceIcon tag={config.artifactTag} size={28} />
        </span>
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-7 flex justify-center px-6">
        <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-black/[0.06] bg-white/75 px-3.5 py-2 text-[11px] font-bold text-gray-600 shadow-sm backdrop-blur-md dark:border-white/10 dark:bg-black/35 dark:text-gray-300">
          <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-seexr-600" />
          <span className="truncate">{stageLabel}</span>
        </div>
      </div>
    </div>
  );
}

function ResultBlueprint({ config }: { config: SearchWorkspaceConfig }) {
  return (
    <div className="flex h-full min-h-[520px] flex-col bg-[#f7f8fa] p-7 dark:bg-[#111112]">
      <div className="mb-5 flex items-center gap-3 border-b border-gray-200 pb-5 dark:border-[#2a2a2e]">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-seexr-solid/10 text-seexr-primary">
          <WorkspaceIcon tag={config.artifactTag} />
        </div>
        <div>
          <p className="text-[13px] font-black text-gray-900 dark:text-white">
            {config.title} 결과
          </p>
          <p className="text-[11px] font-semibold text-gray-400">
            검색이 끝나면 이곳에 {config.resultNoun} 결과가 정리됩니다.
          </p>
        </div>
      </div>
      <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-4 overflow-hidden rounded-[26px] border border-dashed border-gray-300 bg-white/60 p-7 dark:border-[#343439] dark:bg-[#171719]/60">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-[#2d2d31] dark:bg-[#1b1b1e]"
          >
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 shrink-0 rounded-lg bg-seexr-solid/[0.08]" />
              <div className="min-w-0 flex-1">
                <div className="h-3 w-2/3 rounded bg-gray-100 dark:bg-[#303034]" />
                <div className="mt-2 h-2.5 w-1/3 rounded bg-gray-100 dark:bg-[#303034]" />
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <div className="h-2.5 rounded bg-gray-100 dark:bg-[#303034]" />
              <div className="h-2.5 w-5/6 rounded bg-gray-100 dark:bg-[#303034]" />
            </div>
          </div>
        ))}
        <div className="mt-auto rounded-2xl bg-seexr-solid/[0.06] px-5 py-4 text-center text-[12px] font-bold text-seexr-primary">
          왼쪽에서 검색 조건을 입력해 {config.resultNoun} 검색을 시작하세요.
        </div>
      </div>
    </div>
  );
}

function EmptyResultPanel({ config }: { config: SearchWorkspaceConfig }) {
  return (
    <div className="flex h-full min-h-[520px] flex-col items-center justify-center gap-4 bg-[#f7f8fa] p-8 text-center dark:bg-[#111112]">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-seexr-solid/10 text-seexr-primary">
        <WorkspaceIcon tag={config.artifactTag} size={26} />
      </div>
      <div>
        <p className="text-[15px] font-black text-gray-900 dark:text-white">
          표시할 {config.resultNoun} 데이터가 없습니다
        </p>
        <p className="mt-2 text-[12px] font-semibold leading-5 text-gray-400">
          이번 검색에서는 구조화된 {config.resultNoun} 결과가 만들어지지
          않았습니다.
          <br />
          왼쪽의 답변 내용을 확인하거나 조건을 바꿔 다시 검색해보세요.
        </p>
      </div>
    </div>
  );
}

function ReportExpandButton({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={expanded ? "결과 축소" : "결과 전체화면"}
      title={expanded ? "축소" : "전체화면"}
      className="absolute right-5 top-4 z-20 hidden rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 dark:hover:bg-[#2a2a2e] md:block"
    >
      {expanded ? (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
        </svg>
      ) : (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
        </svg>
      )}
    </button>
  );
}

function ProgressPanel({
  config,
  stages,
  state,
  error,
  needsInput,
  finalMessage,
  hasArtifact,
  onCancel,
  onReset,
  onOpenResult,
}: {
  config: SearchWorkspaceConfig;
  stages: SearchRunStage[];
  state: string;
  error: string | null;
  needsInput: boolean;
  finalMessage: string;
  hasArtifact: boolean;
  onCancel: () => void;
  onReset: () => void;
  onOpenResult: () => void;
}) {
  const visibleStages = stages.filter((stage) => stage.id !== "verify");
  const runningStageId =
    visibleStages.find((stage) => stage.status === "running")?.id ?? null;
  const errorStageId =
    visibleStages.find((stage) => stage.status === "error")?.id ?? null;
  const reportStage = visibleStages.find((stage) => stage.id === "report");
  const showFinalMessage =
    Boolean(finalMessage) &&
    (state === "ready" ||
      reportStage?.status === "running" ||
      reportStage?.status === "complete");
  const automaticStageId = runningStageId ?? errorStageId;
  const [stageToggle, setStageToggle] = useState<{
    automaticStageId: SearchRunStage["id"] | null;
    openStageId: SearchRunStage["id"] | null;
  }>({ automaticStageId, openStageId: automaticStageId });
  const openStageId =
    stageToggle.automaticStageId === automaticStageId
      ? stageToggle.openStageId
      : automaticStageId;

  if (state === "failed" || state === "cancelled") {
    return (
      <div className="flex h-full flex-col justify-center p-7 md:p-10">
        <div className="rounded-[28px] border border-amber-200 bg-amber-50/70 p-7 dark:border-amber-900/50 dark:bg-amber-950/15">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-600">
            <AlertTriangle size={23} />
          </div>
          <h2 className="mt-5 text-xl font-black text-gray-950 dark:text-white">
            {needsInput
              ? "추가 정보가 필요합니다"
              : state === "cancelled"
                ? "검색을 취소했습니다"
                : "검색을 완료하지 못했습니다"}
          </h2>
          <p className="mt-3 text-[13px] font-semibold leading-6 text-gray-600 dark:text-gray-300">
            {error}
          </p>
          <button
            type="button"
            onClick={onReset}
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-gray-950 px-4 py-3 text-[13px] font-black text-white dark:bg-white dark:text-gray-950"
          >
            <RotateCcw size={15} />
            조건을 보완해 새 검색
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto bg-white p-6 dark:bg-black md:p-8">
      <div className="flex flex-col gap-3">
        {visibleStages.map((stage) => {
          const isOpen = openStageId === stage.id;
          const isRunning = stage.status === "running";
          const hasDetails = Boolean(stage.summary || stage.actions?.length);
          return (
            <div
              key={stage.id}
              className={`overflow-hidden rounded-2xl border transition-colors ${stageTone[stage.status]}`}
            >
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() =>
                  setStageToggle({
                    automaticStageId,
                    openStageId: isOpen ? null : stage.id,
                  })
                }
                className="flex w-full items-center gap-4 px-4 py-4 text-left md:px-5"
              >
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center text-gray-500 dark:text-gray-400 ${isRunning ? "agent-action-loading-icon" : ""}`}
                >
                  <StageIcon id={stage.id} />
                </div>
                <div className="min-w-0 flex-1">
                  <h3
                    className={`text-[14px] font-black ${isRunning ? "agent-action-loading-text" : "text-gray-800 dark:text-gray-200"}`}
                  >
                    {stage.title}
                  </h3>
                  {stage.durationMs ? (
                    <p className="mt-0.5 text-[10px] font-bold text-gray-400">
                      {durationLabel(stage.durationMs)}
                    </p>
                  ) : null}
                </div>
                <ChevronDown
                  size={17}
                  className={`shrink-0 text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                />
              </button>
              <div
                className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
              >
                <div className="overflow-hidden">
                  <div className="border-t border-gray-100 px-5 pb-5 pt-4 dark:border-[#29292d]">
                    <p className="text-[12px] font-semibold leading-5 text-gray-500 dark:text-gray-400">
                      {stage.summary ||
                        (stage.id === "search"
                          ? `입력한 조건과 관련성이 높은 ${config.resultNoun} 데이터를 검색하고 있습니다.`
                          : `검색 결과를 검토해 ${config.resultNoun} 보고 화면으로 정리하고 있습니다.`)}
                    </p>
                    {hasDetails && stage.actions?.length ? (
                      <div className="mt-4 flex flex-col gap-2">
                        {stage.actions.map((action) => (
                          <ActionPanel key={action.id} action={action} />
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {state === "generating" && (
        <button
          type="button"
          onClick={onCancel}
          className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-3 text-[12px] font-black text-gray-500 hover:border-red-200 hover:text-red-500 dark:border-[#303034]"
        >
          <CircleStop size={15} />
          검색 취소
        </button>
      )}
      {showFinalMessage && (
        <div className="mt-5 rounded-2xl border border-gray-200 bg-white p-5 dark:border-[#2a2a2e] dark:bg-[#18181a]">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 dark:bg-[#29292d] dark:text-gray-400">
              <MessageSquareText size={16} />
            </div>
            <div className="min-w-0 flex-1 text-[13px] font-medium leading-6 text-gray-700 dark:text-gray-300 prose prose-sm max-w-none prose-p:my-1.5 prose-ul:my-2 prose-li:my-0.5 prose-strong:text-gray-900 dark:prose-invert dark:prose-strong:text-white">
              <ReactMarkdown>{finalMessage}</ReactMarkdown>
            </div>
          </div>
          {hasArtifact && (
            <button
              type="button"
              onClick={onOpenResult}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-gray-100 px-4 py-3 text-[12px] font-black text-gray-700 transition-colors hover:bg-gray-200 dark:bg-[#252529] dark:text-gray-200 dark:hover:bg-[#303034] md:hidden"
            >
              <FileOutput size={15} />
              {config.resultNoun} 검색 결과 열기
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function SearchWorkspace({
  agentId,
}: {
  agentId: SearchWorkspaceAgentId;
}) {
  const config = SEARCH_WORKSPACE_CONFIGS[agentId];
  const { languageLabel } = useLanguage();
  const controller = useSearchWorkspaceRunController({ config, languageLabel });
  const { hasMoreSessions, loadAllSessions, sessionsLoading } = controller;
  const [fields, setFields] = useState<Record<string, string>>({});
  const [description, setDescription] = useState("");
  const [modelMode, setModelMode] = useState<"fast" | "pro">("fast");
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [openPopover, setOpenPopover] = useState<string | null>(null);
  const [mobileTab, setMobileTab] = useState<MobileTab>(
    controller.state === "idle" ? "progress" : "result",
  );
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [reportBounds, setReportBounds] = useState<{
    left: number;
    width: number;
  } | null>(null);
  const [isReportExpanded, setIsReportExpanded] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const popoverRootRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const voiceBaseRef = useRef("");
  const reportSlotRef = useRef<HTMLElement>(null);
  const historyLoadRequestedRef = useRef(false);
  const selectedProject =
    controller.projects.find((project) => project.id === selectedProjectId) ??
    null;
  const filteredHistorySessions = useMemo(() => {
    const query = historySearch.trim().toLocaleLowerCase("ko-KR");
    if (!query) return controller.sessions;
    return controller.sessions.filter((session) =>
      `${session.title ?? ""} ${config.title}`
        .toLocaleLowerCase("ko-KR")
        .includes(query),
    );
  }, [config.title, controller.sessions, historySearch]);
  const hasAnyCondition = Boolean(
    description.trim() || Object.values(fields).some((value) => value.trim()),
  );
  const canSubmit = controller.state === "idle" && hasAnyCondition;
  const showWorkspace = controller.state !== "idle";
  const currentStageLabel =
    controller.stages.find((stage) => stage.status === "running")?.title ??
    config.generatingLabel;

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (
        popoverRootRef.current &&
        !popoverRootRef.current.contains(event.target as Node)
      ) {
        setOpenPopover(null);
        setAttachMenuOpen(false);
        setProjectMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 144)}px`;
  }, [description]);

  useEffect(() => () => recognitionRef.current?.stop(), []);

  useEffect(() => {
    if (!isReportExpanded) return;
    const collapseOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setIsReportExpanded(false);
    };
    document.addEventListener("keydown", collapseOnEscape);
    return () => document.removeEventListener("keydown", collapseOnEscape);
  }, [isReportExpanded]);

  useEffect(() => {
    if (!showWorkspace || !reportSlotRef.current) {
      return;
    }
    const slot = reportSlotRef.current;
    const updateBounds = () => {
      const rect = slot.getBoundingClientRect();
      setReportBounds({ left: rect.left, width: rect.width });
    };
    updateBounds();
    const observer = new ResizeObserver(updateBounds);
    observer.observe(slot);
    window.addEventListener("resize", updateBounds);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateBounds);
    };
  }, [showWorkspace]);

  useEffect(() => {
    if (!historyOpen) {
      historyLoadRequestedRef.current = false;
      return;
    }
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        setHistoryOpen(false);
        setHistorySearch("");
      }
    };
    document.addEventListener("keydown", closeOnEscape);
    if (!historyLoadRequestedRef.current && !sessionsLoading) {
      historyLoadRequestedRef.current = true;
      if (hasMoreSessions) {
        void loadAllSessions().catch(() => undefined);
      }
    }
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [hasMoreSessions, historyOpen, loadAllSessions, sessionsLoading]);

  const updateField = (key: string, value: string) => {
    setFields((previous) => ({ ...previous, [key]: value }));
  };

  const stopListening = () => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setIsListening(false);
  };

  const toggleListening = () => {
    if (isListening) {
      stopListening();
      return;
    }
    const speechWindow = window as Window & {
      SpeechRecognition?: SpeechRecognitionConstructor;
      webkitSpeechRecognition?: SpeechRecognitionConstructor;
    };
    const SpeechRecognition =
      speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert(
        "현재 브라우저에서는 음성 인식을 지원하지 않습니다. Chrome 브라우저를 이용해주세요.",
      );
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = "ko-KR";
    recognition.continuous = true;
    recognition.interimResults = true;
    voiceBaseRef.current = description;
    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (event) => {
      let finalTranscript = "";
      let interimTranscript = "";
      for (let index = 0; index < event.results.length; index += 1) {
        const result = event.results[index];
        if (result.isFinal) finalTranscript += `${result[0].transcript} `;
        else interimTranscript += result[0].transcript;
      }
      const prefix = voiceBaseRef.current.trim()
        ? `${voiceBaseRef.current.trim()} `
        : "";
      setDescription(`${prefix}${finalTranscript}${interimTranscript}`);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => {
      recognitionRef.current = null;
      setIsListening(false);
    };
    recognitionRef.current = recognition;
    recognition.start();
  };

  const submit = () => {
    stopListening();
    const prompt = config.buildPrompt(fields, description);
    if (!prompt) {
      alert("검색 조건이나 설명을 입력해주세요.");
      return;
    }
    setMobileTab("result");
    void controller.submit({
      prompt,
      projectId: selectedProjectId,
      modelMode,
    });
  };

  const reset = () => {
    stopListening();
    controller.reset();
    setFields({});
    setDescription("");
    setSelectedProjectId(null);
    setModelMode("fast");
    setAdvancedOpen(false);
    setOpenPopover(null);
    setAttachMenuOpen(false);
    setProjectMenuOpen(false);
    setMobileTab("progress");
    setIsReportExpanded(false);
  };

  const sessionOpen = (session: { id: string; agent_id: string }) => {
    stopListening();
    setHistoryOpen(false);
    setHistorySearch("");
    setMobileTab("result");
    window.history.pushState(
      null,
      "",
      getAgentSessionHref(session.agent_id, session.id),
    );
    void controller.loadSession(session.id);
  };

  const handleComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      event.key !== "Enter" ||
      event.shiftKey ||
      event.nativeEvent.isComposing
    )
      return;
    event.preventDefault();
    if (canSubmit) submit();
  };

  const renderSessionRows = (sessions: AgentSessionListItem[]) => (
    <ul className="flex flex-col border-t-[0.5px] border-gray-200 dark:border-[#2a2a2e]">
      {sessions.map((session) => {
        const when = session.last_message_at ?? session.created_at;
        return (
          <li
            key={session.id}
            className="border-b-[0.5px] border-gray-200 dark:border-[#2a2a2e]"
          >
            <button
              type="button"
              onClick={() => sessionOpen(session)}
              className="group flex w-full items-center gap-4 rounded-xl px-3 py-3 text-left transition-colors hover:bg-gray-50 active:scale-[0.995] dark:hover:bg-[#1f1f22]"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] font-bold text-gray-900 dark:text-gray-100">
                  {session.title || `제목 없는 ${config.title}`}
                </p>
                <p className="mt-1 truncate text-[12px] font-medium text-gray-400">
                  {config.title}
                  {when
                    ? ` · ${new Date(when).toLocaleDateString("ko-KR")}`
                    : ""}
                </p>
              </div>
              <ChevronRight
                size={16}
                className="shrink-0 text-gray-300 transition-transform group-hover:translate-x-0.5 dark:text-gray-600"
              />
            </button>
          </li>
        );
      })}
    </ul>
  );

  const sessionRowsSkeleton = (
    <div
      role="status"
      aria-label={`이전 ${config.title} 기록 불러오는 중`}
      className="flex animate-pulse flex-col border-t-[0.5px] border-gray-200 motion-reduce:animate-none dark:border-[#2a2a2e]"
    >
      <span className="sr-only">
        이전 {config.title} 기록을 불러오는 중입니다.
      </span>
      {Array.from({ length: 3 }).map((_, index) => (
        <div
          key={index}
          aria-hidden="true"
          className="flex items-center gap-4 border-b-[0.5px] border-gray-200 px-3 py-3 dark:border-[#2a2a2e]"
        >
          <div className="min-w-0 flex-1">
            <div
              className={`h-4 rounded-md bg-gray-200/80 dark:bg-[#29292d] ${index === 1 ? "w-[58%]" : index === 2 ? "w-[72%]" : "w-[66%]"}`}
            />
            <div className="mt-2 h-3 w-32 rounded-md bg-gray-100 dark:bg-[#222225]" />
          </div>
          <div className="h-4 w-4 shrink-0 rounded-md bg-gray-100 dark:bg-[#222225]" />
        </div>
      ))}
    </div>
  );

  const inputPanel = (
    <div className="flex min-h-full w-full flex-col bg-white px-5 dark:bg-black md:flex-1 md:justify-center md:px-7 md:py-[clamp(56px,10vh,110px)]">
      <div className="flex min-h-[calc(100dvh-60px)] flex-col py-7 md:min-h-0 md:py-0">
        <div className="flex-1 md:hidden" aria-hidden />
        <div className="flex flex-col items-center pb-8 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-seexr-solid/10 text-seexr-primary">
            <WorkspaceIcon tag={config.artifactTag} size={26} />
          </div>
          <h1 className="mt-4 text-[24px] font-black tracking-tight text-gray-950 dark:text-white">
            {config.title}
          </h1>
          <p className="mt-2 max-w-[480px] text-[13px] font-semibold leading-6 text-gray-400">
            {config.heroSubtitle}
          </p>
        </div>

        <div
          ref={popoverRootRef}
          className="relative isolate shrink-0 rounded-[24px] bg-white before:pointer-events-none before:absolute before:inset-0 before:z-10 before:rounded-[24px] before:border-2 before:border-gray-200 before:content-[''] dark:bg-[#18181a] dark:before:border-[#343439]"
        >
          {selectedProject && (
            <div className="px-4 pt-3">
              <div className="group relative inline-flex max-w-full items-center gap-2 rounded-lg bg-seexr-solid/[0.08] px-2.5 py-1.5 text-[11px] font-bold text-seexr-primary">
                <Folder size={13} className="shrink-0" />
                <span className="truncate">{selectedProject.name}</span>
                <button
                  type="button"
                  onClick={() => setSelectedProjectId(null)}
                  aria-label={`${selectedProject.name} 연결 해제`}
                  className="absolute right-1 flex h-5 w-5 items-center justify-center rounded-full bg-seexr-solid text-white opacity-0 shadow-[0_0_0_3px_rgba(255,255,255,0.9)] transition-all group-hover:opacity-100 focus-visible:opacity-100 dark:shadow-[0_0_0_3px_rgba(29,29,32,0.95)]"
                >
                  <X size={13} strokeWidth={2.8} />
                </button>
              </div>
            </div>
          )}
          <textarea
            ref={textareaRef}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            onKeyDown={handleComposerKeyDown}
            rows={1}
            placeholder={`예: ${SERVICE_PROMPT_PLACEHOLDERS[agentId]}`}
            className="custom-scrollbar block min-h-[24px] max-h-36 w-full resize-none overflow-y-auto bg-transparent px-5 pb-3 pt-4 text-[15px] font-medium leading-6 text-gray-900 outline-none placeholder:text-gray-400 dark:text-gray-100 dark:placeholder:text-gray-500"
          />
          <div className="flex flex-wrap items-end gap-2 px-3 pb-3">
            <div className="relative shrink-0">
              <button
                type="button"
                onClick={() => {
                  setAttachMenuOpen((open) => !open);
                  setProjectMenuOpen(false);
                  setOpenPopover(null);
                }}
                aria-label="추가 메뉴"
                className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors hover:bg-gray-200 dark:bg-[#202022] dark:hover:bg-[#2a2a2e]"
              >
                <Plus size={20} />
              </button>
              {attachMenuOpen && (
                <div className="absolute bottom-[calc(100%+8px)] left-0 z-[100] w-[210px] rounded-2xl border border-gray-100 bg-white py-1.5 shadow-xl animate-in fade-in zoom-in-95 duration-150 dark:border-[#2a2a2e] dark:bg-[#202022]">
                  <button
                    type="button"
                    onClick={() => setProjectMenuOpen((open) => !open)}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-[13px] font-bold text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-[#2a2a2e]"
                  >
                    <Folder size={16} className="text-gray-500" />
                    <span className="flex-1">프로젝트</span>
                    <ChevronRight size={14} className="text-gray-400" />
                  </button>
                </div>
              )}
              {attachMenuOpen && projectMenuOpen && (
                <div className="absolute bottom-[calc(100%+8px)] left-[218px] z-[101] max-h-64 w-[250px] overflow-y-auto rounded-2xl border border-gray-100 bg-white py-1.5 shadow-xl animate-in fade-in zoom-in-95 duration-150 max-sm:left-0 dark:border-[#2a2a2e] dark:bg-[#202022]">
                  {controller.projects.length === 0 ? (
                    <p className="px-4 py-4 text-[12px] font-medium leading-5 text-gray-400">
                      연결할 수 있는 프로젝트가 없습니다.
                    </p>
                  ) : (
                    controller.projects.map((project) => (
                      <button
                        key={project.id}
                        type="button"
                        onClick={() => {
                          setSelectedProjectId(project.id);
                          setAttachMenuOpen(false);
                          setProjectMenuOpen(false);
                        }}
                        className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-[13px] font-bold hover:bg-gray-50 dark:hover:bg-[#2a2a2e] ${selectedProjectId === project.id ? "bg-seexr-solid/10 text-seexr-primary" : "text-gray-700 dark:text-gray-200"}`}
                      >
                        <Folder size={15} className="shrink-0" />
                        <span className="min-w-0 flex-1 truncate">
                          {project.name}
                        </span>
                        {selectedProjectId === project.id && (
                          <Check size={14} />
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {config.badgeFields.map((field) => (
              <div key={field.key} className="relative">
                <SelectionBadge
                  value={fields[field.key] || field.label}
                  selected={Boolean(fields[field.key])}
                  active={openPopover === field.key}
                  onClear={
                    fields[field.key]
                      ? () => {
                          updateField(field.key, "");
                          setOpenPopover(null);
                        }
                      : undefined
                  }
                  onClick={() => {
                    setOpenPopover(
                      openPopover === field.key ? null : field.key,
                    );
                    setAttachMenuOpen(false);
                  }}
                />
                {openPopover === field.key && (
                  <SelectionMenu
                    value={fields[field.key]}
                    options={field.options}
                    onSelect={(value) => {
                      updateField(field.key, value);
                      setOpenPopover(null);
                    }}
                  />
                )}
              </div>
            ))}

            <div className="ml-auto flex shrink-0 items-center gap-1">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setOpenPopover(openPopover === "model" ? null : "model");
                    setAttachMenuOpen(false);
                  }}
                  className="flex h-9 items-center rounded-full px-3.5 text-[13px] font-bold text-gray-600 transition-colors hover:bg-black/5 dark:text-gray-400 dark:hover:bg-white/5"
                >
                  {modelMode === "fast" ? "빠른 답변" : "전문가 모드"}
                  <ChevronDown size={14} className="ml-1.5" />
                </button>
                {openPopover === "model" && (
                  <div className="absolute bottom-[calc(100%+8px)] right-0 z-[100] w-[160px] rounded-2xl border border-gray-100 bg-white py-1.5 shadow-xl animate-in fade-in zoom-in-95 duration-150 dark:border-[#2a2a2e] dark:bg-[#202022]"
                  >
                    <ReasoningStyleSelector />
                    <div className="mx-3 border-t border-gray-100 dark:border-[#2a2a2e]" />
                    {(["fast", "pro"] as const).map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => {
                          setModelMode(mode);
                          setOpenPopover(null);
                        }}
                        className={`flex w-full items-center px-4 py-2.5 text-left text-[13px] font-bold hover:bg-gray-50 dark:hover:bg-[#2a2a2e] ${modelMode === mode ? "text-gray-950 dark:text-white" : "text-gray-600 dark:text-gray-300"}`}
                      >
                        <span>
                          {mode === "fast" ? "빠른 답변" : "전문가 모드"}
                        </span>
                        {modelMode === mode && (
                          <Check
                            size={14}
                            className="ml-auto text-gray-600 dark:text-gray-300"
                          />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setAdvancedOpen((open) => !open)}
                aria-label="상세 조건"
                title="상세 조건"
                className={`flex h-9 w-9 items-center justify-center rounded-full bg-transparent transition-colors ${advancedOpen ? "text-seexr-primary" : "text-gray-500 hover:bg-black/5 dark:hover:bg-white/5"}`}
              >
                <Settings2 size={17} />
              </button>
              <button
                type="button"
                onClick={canSubmit && !isListening ? submit : toggleListening}
                aria-label={
                  isListening
                    ? "음성 입력 중지"
                    : canSubmit
                      ? `${config.title} 시작`
                      : "음성 입력"
                }
                title={
                  isListening
                    ? "음성 입력 중지"
                    : canSubmit
                      ? `${config.title} 시작`
                      : "음성 입력"
                }
                className={`flex h-9 w-9 items-center justify-center rounded-full text-white transition-all hover:scale-105 dark:text-gray-900 ${isListening ? "animate-pulse bg-red-500 hover:bg-red-600" : `bg-gray-900 hover:bg-black dark:bg-white dark:hover:bg-gray-200 ${canSubmit ? "" : "opacity-50 hover:opacity-100"}`}`}
              >
                {isListening ? (
                  <span className="h-3.5 w-3.5 rounded-[3px] bg-current" />
                ) : canSubmit ? (
                  <ArrowUp size={18} strokeWidth={2.6} />
                ) : (
                  <Mic size={18} />
                )}
              </button>
            </div>
          </div>

          {advancedOpen && (
            <div className="grid gap-4 border-t border-gray-100 bg-gray-50/60 px-5 py-5 dark:border-[#29292d] dark:bg-[#151517] sm:grid-cols-2">
              {config.advancedFields.map((field) => (
                <label
                  key={field.key}
                  className="text-[11px] font-black text-gray-500"
                >
                  {field.label}
                  <input
                    value={fields[field.key] ?? ""}
                    onChange={(event) =>
                      updateField(field.key, event.target.value)
                    }
                    placeholder={field.placeholder}
                    className="mt-2 w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-[12px] font-semibold outline-none focus:border-seexr-primary dark:border-[#303034] dark:bg-[#202023]"
                  />
                </label>
              ))}
            </div>
          )}
        </div>

        <section className="pt-7 md:mt-7 md:pt-0">
          {!controller.sessionsInitialized && controller.sessions.length === 0
            ? sessionRowsSkeleton
            : renderSessionRows(controller.sessions.slice(0, 3))}
          {controller.sessionsInitialized &&
            controller.sessions.length === 0 &&
            !controller.sessionsLoading && (
              <p className="py-5 text-center text-[12px] font-medium text-gray-400">
                {config.emptyHistoryLabel}
              </p>
            )}
        </section>
      </div>

      {controller.sessionsInitialized &&
        (controller.sessions.length > 3 || controller.hasMoreSessions) && (
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            className="mt-1 w-full py-2 text-[12px] font-bold text-gray-500 transition-colors hover:text-gray-800 dark:hover:text-gray-200 max-md:mb-7"
          >
            더 보기
          </button>
        )}
    </div>
  );

  const progressPanel = (
    <ProgressPanel
      config={config}
      stages={controller.stages}
      state={controller.state}
      error={controller.error}
      needsInput={controller.needsInput}
      finalMessage={controller.finalAssistantMessage}
      hasArtifact={Boolean(controller.activeArtifact)}
      onCancel={() => void controller.cancel()}
      onReset={reset}
      onOpenResult={() => setMobileTab("result")}
    />
  );

  const closeMobileResult = () => {
    setIsReportExpanded(false);
    setMobileTab("progress");
  };

  const mobileResultCloseButton = (
    <button
      type="button"
      onClick={closeMobileResult}
      aria-label={`${config.title} 결과 닫기`}
      className="absolute right-4 top-4 z-30 rounded-md p-1.5 text-gray-500 transition-colors hover:bg-black/5 dark:text-gray-300 dark:hover:bg-white/10 md:hidden"
    >
      <X size={20} />
    </button>
  );

  const resultPanel =
    controller.state === "loading" ? (
      <div className="relative flex h-full items-center justify-center bg-[#f7f8fa] dark:bg-[#111112]">
        {mobileResultCloseButton}
        <LoaderCircle size={26} className="animate-spin text-seexr-primary" />
      </div>
    ) : controller.state === "generating" ? (
      <div className="relative h-full">
        {mobileResultCloseButton}
        <ReportExpandButton
          expanded={isReportExpanded}
          onToggle={() => setIsReportExpanded((expanded) => !expanded)}
        />
        <GeneratingGraphic config={config} stageLabel={currentStageLabel} />
      </div>
    ) : controller.activeArtifact ? (
      <ArtifactCanvas
        key={controller.activeArtifact.key}
        title={controller.activeArtifact.title}
        type={config.artifactTag}
        xmlContent={controller.activeArtifact.xmlContent}
        onClose={closeMobileResult}
        showCloseButton
        closeButtonClassName="md:hidden"
        isExpanded={isReportExpanded}
        onToggleExpand={() => setIsReportExpanded((expanded) => !expanded)}
        contentWidthClassName="max-w-none"
        onSave={(xml, title) => {
          void controller
            .saveArtifact(xml, title)
            .catch(() => alert("검색 결과를 저장하지 못했습니다."));
        }}
        sessionArtifacts={controller.artifacts.map((artifact) => ({
          key: artifact.key,
          title: artifact.title,
          type: config.artifactTag,
        }))}
        activeArtifactKey={controller.activeArtifact.key}
        onSelectArtifact={controller.setActiveArtifactKey}
      />
    ) : controller.state === "ready" ? (
      <div className="relative h-full">
        {mobileResultCloseButton}
        <EmptyResultPanel config={config} />
      </div>
    ) : (
      <div className="relative h-full">
        {mobileResultCloseButton}
        <ResultBlueprint config={config} />
      </div>
    );

  return (
    <div
      className={`flex h-full min-h-0 w-full flex-col overflow-hidden bg-white pt-[60px] md:pt-0 ${showWorkspace ? "dark:bg-[#111112]" : "dark:bg-black"}`}
      data-search-workspace={config.agentId}
      data-seexr-notice-landing={!showWorkspace ? "true" : undefined}
      data-agent-id={config.agentId}
    >
      {!showWorkspace ? (
        <div className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-y-contain animate-in fade-in duration-300 md:flex md:flex-col">
          <div className="mx-auto min-h-full w-full max-w-[860px] md:flex md:flex-1 md:flex-col">
            {inputPanel}
          </div>
        </div>
      ) : (
        <>
          <div className="flex shrink-0 border-b border-gray-200 bg-white p-2 dark:border-[#2a2a2e] dark:bg-[#151517] md:hidden">
            {(["progress", "result"] as MobileTab[]).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setMobileTab(tab)}
                className={`flex-1 rounded-xl px-3 py-2.5 text-[12px] font-black ${mobileTab === tab ? "bg-gray-950 text-white dark:bg-white dark:text-gray-950" : "text-gray-400"}`}
              >
                {tab === "progress" ? "진행" : "결과"}
              </button>
            ))}
          </div>
          <div className="hidden min-h-0 flex-1 grid-cols-[42%_58%] animate-in fade-in slide-in-from-right-6 duration-500 md:grid">
            <section className="min-h-0 overflow-hidden border-r border-gray-200 bg-white dark:border-[#2a2a2e] dark:bg-black">
              {progressPanel}
            </section>
            <section ref={reportSlotRef} aria-hidden className="min-h-0" />
          </div>
          <section
            className={`fixed top-0 hidden h-[100dvh] overflow-hidden border-l border-gray-200 bg-white shadow-[-20px_0_60px_rgba(15,23,42,0.08)] animate-in fade-in slide-in-from-right-8 duration-500 dark:border-[#2a2a2e] dark:bg-[#111112] md:block ${isReportExpanded ? "z-[100]" : "z-[80]"}`}
            style={
              isReportExpanded
                ? {
                    left: "var(--sidebar-width-desktop, 72px)",
                    width: "calc(100vw - var(--sidebar-width-desktop, 72px))",
                  }
                : reportBounds
                  ? { left: reportBounds.left, width: reportBounds.width }
                  : { right: 0, width: "58vw" }
            }
          >
            {resultPanel}
          </section>
          <div className="min-h-0 flex-1 md:hidden">{progressPanel}</div>
          {mobileTab === "result" && (
            <section
              role="dialog"
              aria-modal="true"
              aria-label={`${config.title} 결과`}
              className="fixed inset-x-0 bottom-0 top-[60px] z-[120] h-[calc(100dvh-60px)] overflow-hidden bg-white animate-in fade-in slide-in-from-bottom-6 duration-300 dark:bg-[#111112] md:hidden"
            >
              {resultPanel}
            </section>
          )}
        </>
      )}

      {historyOpen && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 md:p-8">
          <button
            type="button"
            onClick={() => {
              setHistoryOpen(false);
              setHistorySearch("");
            }}
            aria-label={`${config.historyLabel} 닫기`}
            className="absolute inset-0 cursor-default bg-black/20 backdrop-blur-[6px] animate-in fade-in duration-200 dark:bg-black/45"
          />
          <div
            className="relative flex h-[min(78vh,760px)] w-full max-w-[900px] flex-col overflow-hidden rounded-[28px] border border-gray-200 bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-200 dark:border-[#343439] dark:bg-[#161618]"
            role="dialog"
            aria-modal="true"
            aria-label={config.historyLabel}
          >
            <div className="flex h-[64px] shrink-0 items-center border-b border-gray-200 px-5 dark:border-[#2a2a2e] md:px-7">
              <h2 className="text-[17px] font-black text-gray-900 dark:text-gray-100">
                {config.historyLabel}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setHistoryOpen(false);
                  setHistorySearch("");
                }}
                aria-label="닫기"
                className="ml-auto flex h-9 w-9 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 dark:hover:bg-[#2a2a2e]"
              >
                <X size={19} />
              </button>
            </div>
            <div className="shrink-0 border-b border-gray-100 px-5 py-3 dark:border-[#29292d] md:px-7">
              <div className="relative">
                <Search
                  size={17}
                  className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="search"
                  value={historySearch}
                  onChange={(event) => setHistorySearch(event.target.value)}
                  placeholder={`${config.historyLabel} 검색`}
                  autoFocus
                  className="h-10 w-full rounded-full border border-gray-200 bg-gray-50 pl-10 pr-4 text-[13px] font-medium text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-gray-400 dark:border-[#303034] dark:bg-[#202022] dark:text-gray-100 dark:focus:border-gray-500"
                />
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 md:px-7 md:py-6">
              {(!controller.sessionsInitialized ||
                controller.sessionsLoading) &&
              controller.sessions.length === 0
                ? sessionRowsSkeleton
                : renderSessionRows(filteredHistorySessions)}
              {controller.sessionsInitialized &&
                !controller.sessionsLoading &&
                controller.hasMoreSessions && (
                  <button
                    type="button"
                    onClick={() => {
                      historyLoadRequestedRef.current = true;
                      void controller.loadAllSessions().catch(() => undefined);
                    }}
                    className="mt-4 w-full rounded-xl py-3 text-[12px] font-bold text-gray-500 hover:bg-gray-50 dark:hover:bg-[#202022]"
                  >
                    기록 다시 불러오기
                  </button>
                )}
              {controller.sessionsInitialized &&
                !controller.sessionsLoading &&
                filteredHistorySessions.length === 0 && (
                  <p className="py-20 text-center text-[13px] font-medium text-gray-400">
                    {historySearch.trim()
                      ? "검색 결과가 없습니다."
                      : config.emptyHistoryLabel}
                  </p>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
